import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { dropTask } from 'ember-concurrency';
import type { Task } from 'ember-concurrency';
import type { EncapsulatedTask } from 'ember-concurrency';
import FocusTrapper from 'ember-focus-trapper/components/focus-trapper';
import ReservationDeleteConfirmation from 'garaje/components/reservations/reservation-delete-confirmation';
import ModalDialog from 'garaje/components/ui/classic/modal-dialog';
import UiButton from 'garaje/components/ui-button';
import type Entry from 'garaje/models/entry';
import type Invite from 'garaje/models/invite';

interface DeleteConfirmationModalSignature {
  Args: {
    deleteReservationTask: Task<void, [reservation?: Entry | Invite]>;
    showDeleteConfirmationTask: EncapsulatedTask<
      unknown,
      [Entry | Invite | undefined],
      { abort: () => void; continue: () => void; args: [Entry | Invite | undefined] }
    >;
  };
}

export default class DeleteConfirmationModal extends Component<DeleteConfirmationModalSignature> {
  deleteReservationTask = dropTask(async () => {
    const { deleteReservationTask, showDeleteConfirmationTask } = this.args;
    const { last } = showDeleteConfirmationTask;

    try {
      await deleteReservationTask.perform(last?.args[0]);
      last?.continue();
    } catch (error) {
      last?.abort();
      throw error;
    }
  });

  @action
  abort(): void {
    this.args.showDeleteConfirmationTask.last?.abort();
  }

  <template>
    {{#if @showDeleteConfirmationTask.isRunning}}
      <ModalDialog
        @clickOutsideToClose={{false}}
        @translucentOverlay={{true}}
        @containerClass="ember-modal-dialog--reservation"
      >
        <FocusTrapper>
          <div class="flex justify-between items-center">
            <h3>Delete reservation?</h3>
            <div>
              <UiButton
                data-test-close-modal
                class="btn btn--square btn--outlineFill !bg-white icon-cross-small-gray email"
                name="close"
                aria-label="Close delete confirmation dialog"
                {{on "click" this.abort}}
              />
            </div>
          </div>
          <ReservationDeleteConfirmation
            @deleteReservationTask={{this.deleteReservationTask}}
            @onClose={{this.abort}}
          />
        </FocusTrapper>
      </ModalDialog>
    {{/if}}
  </template>
}
